import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardBody, Badge, Button, Input } from '@img/ui';
import { api, type Paginated } from '../../lib/api';

interface AuctionRequestRow {
  id: string;
  name: string;
  phone: string;
  email: string | null;
  propertyTitle: string;
  propertyType: string | null;
  city: string | null;
  area: number | null;
  expectedPrice: number | string | null;
  description: string | null;
  notes: string | null;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'SCHEDULED';
  rejectReason: string | null;
  scheduledStart: string | null;
  scheduledEnd:   string | null;
  auctionId: string | null;
  createdAt: string;
}

const STATUS: Record<string, { label: string; color: 'amber' | 'green' | 'red' | 'navy' }> = {
  PENDING:   { label: 'قيد المراجعة', color: 'amber' },
  APPROVED:  { label: 'موافق',         color: 'green' },
  REJECTED:  { label: 'مرفوض',         color: 'red' },
  SCHEDULED: { label: 'مجدول',         color: 'navy' },
};

export function AdminAuctionRequestsPage() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('PENDING');
  const [openId, setOpenId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'auction-requests', statusFilter],
    queryFn: () => api.get<Paginated<AuctionRequestRow>>('/api/auction-requests', { query: { status: statusFilter, limit: 50 } }),
  });

  const detail = data?.data.find((r) => r.id === openId) ?? null;

  return (
    <div>
      <h1 className="text-3xl font-black text-navy-dark mb-2">طلبات إقامة المزادات</h1>
      <p className="text-gray-600 mb-6">طلبات المستخدمين لإقامة مزاد على عقاراتهم</p>

      <div className="flex gap-2 mb-6 overflow-x-auto">
        <FilterTab label="قيد المراجعة" active={statusFilter === 'PENDING'}   onClick={() => setStatusFilter('PENDING')} />
        <FilterTab label="مجدول"        active={statusFilter === 'SCHEDULED'} onClick={() => setStatusFilter('SCHEDULED')} />
        <FilterTab label="مرفوض"        active={statusFilter === 'REJECTED'}  onClick={() => setStatusFilter('REJECTED')} />
        <FilterTab label="الكل"         active={!statusFilter}                onClick={() => setStatusFilter('')} />
      </div>

      <Card>
        <CardBody className="!p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <Th>المتقدّم</Th>
                <Th>العقار</Th>
                <Th>المدينة</Th>
                <Th>المساحة</Th>
                <Th>السعر المتوقع</Th>
                <Th>التاريخ</Th>
                <Th>الحالة</Th>
                <Th>إجراءات</Th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={8} className="text-center py-12 text-gray-500">⟳ جاري التحميل…</td></tr>
              ) : !data?.data?.length ? (
                <tr><td colSpan={8} className="text-center py-12 text-gray-500">لا توجد طلبات</td></tr>
              ) : data.data.map((r) => (
                <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer" onClick={() => setOpenId(r.id)}>
                  <Td>
                    <div className="font-bold">{r.name}</div>
                    <div className="text-xs text-gray-500" dir="ltr">{r.phone}</div>
                  </Td>
                  <Td className="line-clamp-1">{r.propertyTitle}</Td>
                  <Td>{r.city ?? '—'}</Td>
                  <Td>{r.area ? `${r.area} م²` : '—'}</Td>
                  <Td className="font-bold">{r.expectedPrice ? `${Number(r.expectedPrice).toLocaleString('en-US')} ر.س` : '—'}</Td>
                  <Td className="text-xs">{new Date(r.createdAt).toLocaleString('ar-SA')}</Td>
                  <Td><Badge color={STATUS[r.status]?.color ?? 'amber'}>{STATUS[r.status]?.label ?? r.status}</Badge></Td>
                  <Td><Button size="sm" variant="primary">عرض</Button></Td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardBody>
      </Card>

      {detail && (
        <DetailModal
          request={detail}
          onClose={() => setOpenId(null)}
          onChanged={() => {
            qc.invalidateQueries({ queryKey: ['admin', 'auction-requests'] });
            qc.invalidateQueries({ queryKey: ['admin', 'auctions'] });
            setOpenId(null);
          }}
        />
      )}
    </div>
  );
}

function DetailModal({ request, onClose, onChanged }: { request: AuctionRequestRow; onClose: () => void; onChanged: () => void }) {
  const [openingPrice, setOpeningPrice] = useState(String(request.expectedPrice ?? ''));
  const [bidIncrement, setBidIncrement] = useState('50000');
  const [depositAmount, setDepositAmount] = useState('50000');
  const [start, setStart] = useState(new Date(Date.now() + 60 * 60 * 1000).toISOString().slice(0, 16));
  const [end,   setEnd]   = useState(new Date(Date.now() + 25 * 60 * 60 * 1000).toISOString().slice(0, 16));
  const [rejectReason, setRejectReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const approve = useMutation({
    mutationFn: () => api.post(`/api/auction-requests/${request.id}/approve`, {
      scheduledStart: start, scheduledEnd: end,
      openingPrice: Number(openingPrice),
      bidIncrement: Number(bidIncrement),
      depositAmount: Number(depositAmount),
    }),
    onSuccess: onChanged,
    onError: (e) => setErr((e as Error).message),
  });

  const reject = useMutation({
    mutationFn: () => api.post(`/api/auction-requests/${request.id}/reject`, { reason: rejectReason }),
    onSuccess: onChanged,
    onError: (e) => setErr((e as Error).message),
  });

  const isPending = request.status === 'PENDING';

  return (
    <div className="fixed inset-0 bg-black/60 z-50 overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full mx-auto my-4" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white rounded-t-2xl">
          <h2 className="font-black text-xl text-navy-dark">📨 تفاصيل الطلب</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl">✕</button>
        </div>

        <div className="p-5 space-y-5">
          {/* 👤 معلومات المتقدّم */}
          <Section title="👤 المتقدّم">
            <Row label="الاسم"  value={request.name} />
            <Row label="الجوال"  value={request.phone} dir="ltr" />
            <Row label="الإيميل" value={request.email ?? '—'} dir="ltr" />
          </Section>

          {/* 🏠 معلومات العقار */}
          <Section title="🏠 العقار">
            <Row label="العنوان"     value={request.propertyTitle} />
            <Row label="النوع"        value={request.propertyType ?? '—'} />
            <Row label="المدينة"     value={request.city ?? '—'} />
            <Row label="المساحة"      value={request.area ? `${request.area} م²` : '—'} />
            <Row label="السعر المتوقع" value={request.expectedPrice ? `${Number(request.expectedPrice).toLocaleString('en-US')} ر.س` : '—'} />
            {request.description && <Row label="الوصف" value={request.description} multi />}
            {request.notes       && <Row label="ملاحظات" value={request.notes} multi />}
          </Section>

          {request.status === 'SCHEDULED' && request.auctionId && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <div className="font-black text-green-800">✅ تمت الموافقة وجُدول المزاد</div>
              <div className="text-sm text-green-700 mt-1">
                موعد البداية: {new Date(request.scheduledStart!).toLocaleString('ar-SA')}<br/>
                موعد النهاية: {new Date(request.scheduledEnd!).toLocaleString('ar-SA')}
              </div>
              <a href={`/admin/auctions`} className="inline-block mt-3 text-sm font-bold text-navy-dark hover:underline">
                ← اذهب لإدارة المزادات
              </a>
            </div>
          )}

          {request.status === 'REJECTED' && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <div className="font-black text-red-800">🚫 تم رفض الطلب</div>
              {request.rejectReason && <div className="text-sm text-red-700 mt-1">السبب: {request.rejectReason}</div>}
            </div>
          )}

          {isPending && (
            <>
              {/* ✅ جدولة وموافقة */}
              <Section title="✅ جدولة المزاد (موافقة)">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                  <Input label="سعر الافتتاح (ر.س) *" type="number" value={openingPrice} onChange={(e) => setOpeningPrice(e.target.value)} required />
                  <Input label="مقدار الزيادة (ر.س)"   type="number" value={bidIncrement} onChange={(e) => setBidIncrement(e.target.value)} />
                  <Input label="مبلغ التأمين (ر.س)"     type="number" value={depositAmount} onChange={(e) => setDepositAmount(e.target.value)} />
                  <div /> {/* spacer */}
                  <Input label="تاريخ البداية *" type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} required />
                  <Input label="تاريخ النهاية *" type="datetime-local" value={end}   onChange={(e) => setEnd(e.target.value)} required />
                </div>
              </Section>

              {err && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">{err}</div>}

              <div className="flex flex-wrap gap-2 justify-between border-t pt-4">
                <div className="flex gap-2">
                  <Input placeholder="سبب الرفض (اختياري)" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
                  <Button variant="danger" loading={busy && reject.isPending} onClick={() => { setBusy(true); reject.mutate(undefined, { onSettled: () => setBusy(false) }); }}>🚫 رفض</Button>
                </div>
                <Button variant="gold" size="lg" loading={busy && approve.isPending} onClick={() => { setBusy(true); approve.mutate(undefined, { onSettled: () => setBusy(false) }); }}>
                  ✅ وافق وجدول المزاد
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <fieldset className="border border-gray-200 rounded-xl p-4">
      <legend className="px-2 font-black text-navy-dark text-sm">{title}</legend>
      <div className="space-y-2">{children}</div>
    </fieldset>
  );
}

function Row({ label, value, dir, multi }: { label: string; value: string; dir?: string; multi?: boolean }) {
  return (
    <div className={multi ? '' : 'flex items-center justify-between'}>
      <span className="text-xs text-gray-500 shrink-0">{label}:</span>
      <span className={`font-bold text-sm text-ink ${multi ? 'block mt-1 whitespace-pre-line' : ''}`} dir={dir}>{value}</span>
    </div>
  );
}

function FilterTab({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick} className={`px-4 py-2 rounded-xl font-bold text-sm transition shrink-0 ${
      active ? 'bg-navy-dark text-white' : 'bg-white text-gray-600 hover:bg-gray-100'
    }`}>{label}</button>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="text-right px-4 py-3 font-bold text-gray-600 text-xs uppercase">{children}</th>;
}
function Td({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-4 py-3 ${className}`}>{children}</td>;
}
